/**
 * Filtros do Mapa da Produção — um só conjunto vale para todas as visões
 * (mapa, kanban, tabela, calendário, timeline) e para ambientes, tarefas e
 * PEÇAS DESMEMBRADAS.
 *
 * Cada filtro sabe se aplicar às três coisas. Quando o filtro é de ambiente
 * (cliente, lote, etapa, pendência…), a tarefa e a peça passam se o AMBIENTE
 * delas passa — tarefa sem ambiente não tem cliente e sai. Quando é de tarefa
 * (status da tarefa), o ambiente passa se tiver alguma tarefa que passe.
 *
 * O filtro `tipoItem` é de outra natureza: ele não escolhe QUAIS itens, e sim
 * QUE TIPO de item aparece (ambiente, lote, tarefa, peça). O tipo que fica de
 * fora volta como lista vazia, e `tiposDeItemAtivos` diz para a tela quais
 * blocos desenhar — "lote" e "ambiente" andam juntos porque o lote é um
 * agrupamento dos ambientes (`lotes.js`).
 *
 * Os filtros ficam salvos no navegador por unidade (tenantStorage). A busca
 * livre não é salva: é do momento, e voltar à tela com um texto esquecido
 * filtrando tudo seria mais confuso do que útil.
 */
import { normalizeText } from "../../../utils/data";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { getItemTenant, removeItemTenant, setItemTenant } from "../../../utils/tenantStorage";
import { startOfDay, toDate } from "../../../utils/status";
import { criarCatalogoSetores } from "../setores";
import { indexarEstado } from "./combinar";
import { ETAPAS_PARALELAS, ETAPAS_SEQUENCIAIS } from "./etapasPlanilha";
import { ROTULO_STATUS_AMBIENTE, ROTULO_STATUS_TAREFA, tarefaAtrasada } from "./vocabulario";

/**
 * A chave mudou de versão na v3: o filtro de setor guardava os CÓDIGOS fixos
 * (corte, bordo…) e agora guarda os ids do cadastro. Reler o valor antigo
 * filtraria tudo para fora sem explicação — melhor começar limpo.
 */
export const CHAVE_FILTROS = "pharuerp:mapa-producao:filtros:v3";

// Valor de filtro que casa com "não tem" (sem lote, sem posto, sem colaborador…).
export const SEM_VALOR = "__sem__";

/** Tipos de item que o Mapa mostra. */
export const TIPO_ITEM = Object.freeze({
  AMBIENTE: "ambiente",
  LOTE: "lote",
  TAREFA: "tarefa",
  PECA: "peca",
});

const TIPOS_DE_ITEM = Object.freeze([
  { valor: TIPO_ITEM.AMBIENTE, rotulo: "Ambientes" },
  { valor: TIPO_ITEM.LOTE, rotulo: "Lotes" },
  { valor: TIPO_ITEM.TAREFA, rotulo: "Tarefas" },
  { valor: TIPO_ITEM.PECA, rotulo: "Peças desmembradas" },
]);

// O lote é um agrupamento dos ambientes: escolher "Lotes" não pode esvaziar as
// demandas, senão não há o que agrupar.
const TIPOS_QUE_USAM_DEMANDAS = [TIPO_ITEM.AMBIENTE, TIPO_ITEM.LOTE];

const NAO_PERSISTIR = ["busca"];
const RE_DATA = /^\d{4}-\d{2}-\d{2}$/;

export const GRUPOS_FILTROS = Object.freeze([
  { id: "tipo", rotulo: "O que mostrar" },
  { id: "local", rotulo: "Onde" },
  { id: "pessoas", rotulo: "Quem" },
  { id: "ambiente", rotulo: "Ambiente" },
  { id: "situacao", rotulo: "Situação" },
  { id: "prazo", rotulo: "Prazo" },
]);

const opcoesFixas = (rotulos) => Object.entries(rotulos).map(([valor, rotulo]) => ({ valor, rotulo }));

// Pendência = etapa PARALELA da planilha em aberto (terceiros, acabamento,
// insumos). Sai do catálogo para a lista não envelhecer escondida aqui.
const OPCOES_PENDENCIA = ETAPAS_PARALELAS.map((etapa) => ({ valor: etapa.codigo, rotulo: etapa.rotulo }));

/**
 * Definição declarativa: a barra de filtros se monta a partir daqui.
 * tipo: "texto" | "multipla" (lista de valores) | "opcao" (um valor ou "")
 *       | "periodo" ({de, ate} em "YYYY-MM-DD").
 */
export const DEFINICOES_FILTROS = Object.freeze([
  { id: "busca", rotulo: "Busca", tipo: "texto", grupo: null },
  { id: "tipoItem", rotulo: "Tipo de item", tipo: "multipla", grupo: "tipo", opcoes: TIPOS_DE_ITEM },
  { id: "setores", rotulo: "Setor", tipo: "multipla", grupo: "local" },
  { id: "etapas", rotulo: "Etapa da planilha", tipo: "multipla", grupo: "local" },
  { id: "subsetores", rotulo: "Subsetor", tipo: "multipla", grupo: "local" },
  { id: "postos", rotulo: "Posto", tipo: "multipla", grupo: "local" },
  { id: "colaboradores", rotulo: "Colaborador", tipo: "multipla", grupo: "pessoas" },
  { id: "clientes", rotulo: "Cliente", tipo: "multipla", grupo: "ambiente" },
  { id: "pedidos", rotulo: "Pedido", tipo: "multipla", grupo: "ambiente" },
  { id: "lotes", rotulo: "Lote", tipo: "multipla", grupo: "ambiente" },
  { id: "pendencias", rotulo: "Pendência", tipo: "multipla", grupo: "ambiente", opcoes: OPCOES_PENDENCIA },
  {
    id: "statusAmbiente",
    rotulo: "Status do ambiente",
    tipo: "multipla",
    grupo: "situacao",
    opcoes: opcoesFixas(ROTULO_STATUS_AMBIENTE),
  },
  {
    id: "statusTarefa",
    rotulo: "Status da tarefa",
    tipo: "multipla",
    grupo: "situacao",
    opcoes: opcoesFixas(ROTULO_STATUS_TAREFA),
  },
  {
    id: "tarefas",
    rotulo: "Tarefas",
    tipo: "opcao",
    grupo: "situacao",
    opcoes: [
      { valor: "com", rotulo: "Com tarefa aberta" },
      { valor: "sem", rotulo: "Sem tarefa aberta" },
    ],
  },
  {
    id: "pecasFora",
    rotulo: "Peças desmembradas",
    tipo: "opcao",
    grupo: "situacao",
    opcoes: [
      { valor: "com", rotulo: "Com peça fora" },
      { valor: "sem", rotulo: "Sem peça fora" },
    ],
  },
  {
    id: "atrasadas",
    rotulo: "Atraso",
    tipo: "opcao",
    grupo: "prazo",
    opcoes: [
      { valor: "sim", rotulo: "Só atrasadas" },
      { valor: "nao", rotulo: "Sem atraso" },
    ],
  },
  { id: "prazo", rotulo: "Prazo entre", tipo: "periodo", grupo: "prazo" },
]);

const DEFINICAO_POR_ID = new Map(DEFINICOES_FILTROS.map((d) => [d.id, d]));

function valorVazio(tipo) {
  if (tipo === "multipla") return [];
  if (tipo === "periodo") return { de: "", ate: "" };
  return "";
}

/** Objeto novo com todos os filtros vazios (nunca compartilha arrays). */
export function criarFiltrosVazios() {
  return Object.fromEntries(DEFINICOES_FILTROS.map((d) => [d.id, valorVazio(d.tipo)]));
}

function normalizarValor(definicao, bruto) {
  switch (definicao.tipo) {
    case "texto":
      return typeof bruto === "string" ? bruto : "";
    case "multipla":
      return Array.isArray(bruto) ? [...new Set(bruto.filter((v) => typeof v === "string" && v))] : [];
    case "opcao":
      return definicao.opcoes.some((o) => o.valor === bruto) ? bruto : "";
    case "periodo":
      return {
        de: RE_DATA.test(bruto?.de || "") ? bruto.de : "",
        ate: RE_DATA.test(bruto?.ate || "") ? bruto.ate : "",
      };
    default:
      return valorVazio(definicao.tipo);
  }
}

/** Filtros confiáveis a partir de qualquer coisa (localStorage velho, URL…). */
export function normalizarFiltros(bruto) {
  const origem = bruto && typeof bruto === "object" ? bruto : {};
  return Object.fromEntries(DEFINICOES_FILTROS.map((d) => [d.id, normalizarValor(d, origem[d.id])]));
}

function filtroAtivo(definicao, valor) {
  if (definicao.tipo === "texto") return Boolean(String(valor || "").trim());
  if (definicao.tipo === "multipla") return Array.isArray(valor) && valor.length > 0;
  if (definicao.tipo === "periodo") return Boolean(valor?.de || valor?.ate);
  return Boolean(valor);
}

/** Quantos filtros estão em uso (a busca conta como um). */
export function contarAtivos(filtros) {
  const f = normalizarFiltros(filtros);
  return DEFINICOES_FILTROS.filter((d) => filtroAtivo(d, f[d.id])).length;
}

/** Filtros com um deles zerado (o "x" do chip). */
export function removerFiltro(filtros, id) {
  const definicao = DEFINICAO_POR_ID.get(id);
  const f = normalizarFiltros(filtros);
  return definicao ? { ...f, [id]: valorVazio(definicao.tipo) } : f;
}

/**
 * Tipos de item a desenhar: todos quando o filtro está vazio (é o padrão — o
 * gestor não precisa escolher para ver o mapa inteiro).
 */
export function tiposDeItemAtivos(filtros) {
  const escolhidos = normalizarFiltros(filtros).tipoItem;
  return escolhidos.length ? escolhidos : TIPOS_DE_ITEM.map((t) => t.valor);
}

// ------------------------------------------------------------ persistência

export function lerFiltrosSalvos() {
  try {
    return normalizarFiltros(JSON.parse(getItemTenant(CHAVE_FILTROS) || "{}"));
  } catch {
    // JSON corrompido: começa limpo em vez de quebrar a tela.
    return criarFiltrosVazios();
  }
}

export function salvarFiltros(filtros) {
  const f = normalizarFiltros(filtros);
  NAO_PERSISTIR.forEach((id) => delete f[id]);
  setItemTenant(CHAVE_FILTROS, JSON.stringify(f));
}

export function limparFiltrosSalvos() {
  removeItemTenant(CHAVE_FILTROS);
}

// ------------------------------------------------------------------ opções

function distintos(demandas, campo, { comSemValor = false, rotuloSem = "" } = {}) {
  const porValor = new Map();
  let temVazio = false;
  (demandas || []).forEach((d) => {
    const texto = String(d[campo] ?? "").trim();
    const valor = normalizeText(texto);
    if (!valor) temVazio = true;
    else if (!porValor.has(valor)) porValor.set(valor, texto);
  });
  const opcoes = [...porValor.entries()]
    .map(([valor, rotulo]) => ({ valor, rotulo }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR", { numeric: true }));
  return comSemValor && temVazio ? [{ valor: SEM_VALOR, rotulo: rotuloSem }, ...opcoes] : opcoes;
}

function catalogoDo(contexto) {
  return contexto.catalogo || criarCatalogoSetores(contexto.estado?.setores);
}

// Subsetor e posto pertencem a um setor cadastrado: o rótulo mostra os dois
// ("Corte › Serra 1") e a ordem segue a do mapa.
function opcoesDoCadastro(itens, rotuloSem, catalogo) {
  const opcoes = [...(itens || [])]
    .sort(
      (a, b) =>
        catalogo.ordem(a.setor_id) - catalogo.ordem(b.setor_id) ||
        (a.ordem ?? 0) - (b.ordem ?? 0) ||
        String(a.nome).localeCompare(String(b.nome), "pt-BR")
    )
    .map((item) => {
      const setor = catalogo.nome(item.setor_id);
      return {
        valor: item.id,
        rotulo: setor ? `${setor} › ${item.nome}` : item.nome,
        inativo: item.ativo === false,
      };
    });
  return [{ valor: SEM_VALOR, rotulo: rotuloSem }, ...opcoes];
}

function opcoesDeColaborador(colaboradores) {
  const opcoes = [...(colaboradores || [])]
    .sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"))
    .map((item) => ({ valor: item.id, rotulo: item.nome, inativo: item.ativo === false }));
  return [{ valor: SEM_VALOR, rotulo: "Sem colaborador" }, ...opcoes];
}

/**
 * Opções de um filtro: [{valor, rotulo, inativo?}]. `contexto` =
 * {demandas (combinadas), estado (do useEstadoMapa), catalogo}.
 */
export function opcoesDoFiltro(id, contexto = {}) {
  const definicao = DEFINICAO_POR_ID.get(id);
  if (!definicao) return [];
  if (definicao.opcoes) return definicao.opcoes;
  const { demandas = [], estado = null } = contexto;
  switch (id) {
    case "setores":
      return catalogoDo(contexto).setores.map((s) => ({ valor: s.id, rotulo: s.nome }));
    case "etapas":
      return ETAPAS_SEQUENCIAIS.map((e) => ({ valor: e.codigo, rotulo: e.rotulo }));
    case "subsetores":
      return opcoesDoCadastro(estado?.subsetores, "Sem subsetor", catalogoDo(contexto));
    case "postos":
      return opcoesDoCadastro(estado?.postos, "Sem posto", catalogoDo(contexto));
    case "colaboradores":
      return opcoesDeColaborador(estado?.colaboradores);
    case "clientes":
      return distintos(demandas, "cliente");
    case "pedidos":
      return distintos(demandas, "pedido");
    case "lotes":
      return distintos(demandas, "lote", { comSemValor: true, rotuloSem: "Sem lote" });
    default:
      return [];
  }
}

function textoDoPeriodo({ de, ate }) {
  if (de && ate) return `${formatarDataLocal(de)} a ${formatarDataLocal(ate)}`;
  return de ? `a partir de ${formatarDataLocal(de)}` : `até ${formatarDataLocal(ate)}`;
}

function textoDoValor(definicao, valor, contexto) {
  if (definicao.tipo === "texto") return `“${valor.trim()}”`;
  if (definicao.tipo === "periodo") return textoDoPeriodo(valor);
  const rotulos = new Map(opcoesDoFiltro(definicao.id, contexto).map((o) => [o.valor, o.rotulo]));
  const lista = Array.isArray(valor) ? valor : [valor];
  return lista.map((v) => rotulos.get(v) || v).join(", ");
}

/** Chips dos filtros em uso: [{id, rotulo, valor}] (valor já em texto). */
export function resumoDosFiltros(filtros, contexto = {}) {
  const f = normalizarFiltros(filtros);
  return DEFINICOES_FILTROS.filter((d) => filtroAtivo(d, f[d.id])).map((d) => ({
    id: d.id,
    rotulo: d.rotulo,
    valor: textoDoValor(d, f[d.id], contexto),
  }));
}

// -------------------------------------------------------------- aplicação

function montarContexto(demandas, tarefas, estado, hoje) {
  const indice = indexarEstado(estado);
  const demandaPorChave = new Map();
  demandas.forEach((d) => {
    if (d.chave && !demandaPorChave.has(d.chave)) demandaPorChave.set(d.chave, d);
  });
  const tarefasPorAmbiente = new Map();
  tarefas.forEach((t) => {
    if (!t.ambiente_chave) return;
    if (!tarefasPorAmbiente.has(t.ambiente_chave)) tarefasPorAmbiente.set(t.ambiente_chave, []);
    tarefasPorAmbiente.get(t.ambiente_chave).push(t);
  });
  return { indice, demandaPorChave, tarefasPorAmbiente, hoje };
}

// Vazio casa com SEM_VALOR; senão, com o próprio valor.
function casa(conjunto, valor) {
  return valor ? conjunto.has(valor) : conjunto.has(SEM_VALOR);
}

function casaAlgum(conjunto, valores) {
  return valores.length ? valores.some((v) => conjunto.has(v)) : conjunto.has(SEM_VALOR);
}

// Demanda que não passou por combinarDemandas não tem `tarefas`.
const tarefasDe = (item) => item.tarefas || [];

function colaboradoresDaDemanda(d, ctx) {
  const ids = new Set(tarefasDe(d).map((t) => t.colaborador_id).filter(Boolean));
  (ctx.indice.postos.get(d.postoId)?.colaborador_ids || []).forEach((id) => ids.add(id));
  if (d.colaboradorPlanilhaId) ids.add(d.colaboradorPlanilhaId);
  return [...ids];
}

function dentroDoPeriodo(data, { de, ate }) {
  const dia = startOfDay(data);
  if (!dia) return false;
  if (de && dia < toDate(de)) return false;
  if (ate && dia > toDate(ate)) return false;
  return true;
}

const nomeDe = (mapa, id) => mapa.get(id)?.nome || "";

function textoDaDemanda(d, ctx) {
  const { indice } = ctx;
  const posto = indice.postos.get(d.postoId);
  return normalizeText(
    [
      d.cliente,
      d.pedido,
      d.ambiente,
      d.lote,
      d.rotulo,
      d.marceneiro?.nome,
      d.setorNome,
      d.etapaRotulo,
      nomeDe(indice.subsetores, d.subsetorId),
      posto?.nome,
      ...colaboradoresDaDemanda(d, ctx).map((id) => nomeDe(indice.colaboradores, id)),
      ...tarefasDe(d).flatMap((t) => [t.codigo, t.titulo]),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function textoDaTarefa(t, ctx) {
  const { indice } = ctx;
  return normalizeText(
    [
      t.codigo,
      t.titulo,
      t.descricao,
      t.ambiente_rotulo,
      nomeDe(indice.colaboradores, t.colaborador_id),
      nomeDe(indice.postos, t.posto_id),
      nomeDe(indice.subsetores, t.subsetor_id),
      nomeDe(indice.pecas, t.peca_id),
      ctx.demandaPorChave.get(t.ambiente_chave)?.cliente,
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function textoDaPeca(p, ctx) {
  const { indice } = ctx;
  return normalizeText(
    [
      p.codigo,
      p.nome,
      p.ambienteRotulo,
      p.cliente,
      p.setorNome,
      nomeDe(indice.subsetores, p.subsetorId),
      nomeDe(indice.postos, p.postoId),
      nomeDe(indice.colaboradores, p.colaboradorId),
      ...tarefasDe(p).flatMap((t) => [t.codigo, t.titulo]),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

const ambienteDaTarefa = (t, ctx) => (t.ambiente_chave ? ctx.demandaPorChave.get(t.ambiente_chave) : null);
const ambienteDaPeca = (p, ctx) => p.ambiente || (p.ambienteChave ? ctx.demandaPorChave.get(p.ambienteChave) : null);

// Filtro de ambiente aplicado à tarefa/peça: vale o ambiente dela.
function viaAmbiente(predicado, achar) {
  return (item, ctx) => {
    const demanda = achar(item, ctx);
    return Boolean(demanda) && predicado(demanda, ctx);
  };
}

function porCampoNormalizado(campo) {
  return (valores) => {
    const conjunto = new Set(valores);
    return { demanda: (d) => casa(conjunto, normalizeText(d[campo])) };
  };
}

/**
 * Para cada filtro: valor → {demanda?, tarefa?, peca?} predicados. Sem
 * predicado para um lado, o filtro não se aplica a ele (passa).
 */
const CRITERIOS = {
  busca: (texto) => {
    const palavras = normalizeText(texto).split(" ").filter(Boolean);
    const contem = (alvo) => palavras.every((p) => alvo.includes(p));
    return {
      demanda: (d, ctx) => contem(textoDaDemanda(d, ctx)),
      tarefa: (t, ctx) => contem(textoDaTarefa(t, ctx)),
      peca: (p, ctx) => contem(textoDaPeca(p, ctx)),
    };
  },
  // `tipoItem` escolhe QUE TIPO aparece, não quais itens: quem aplica é
  // `aplicarFiltros`, esvaziando a coleção do tipo que ficou de fora.
  tipoItem: () => ({}),
  setores: (valores) => {
    const conjunto = new Set(valores);
    return {
      demanda: (d) => casa(conjunto, d.setorId),
      tarefa: (t) => casa(conjunto, t.setor_id),
      peca: (p) => casa(conjunto, p.setorId),
    };
  },
  etapas: (valores) => {
    const conjunto = new Set(valores);
    return { demanda: (d) => casa(conjunto, d.etapa) };
  },
  subsetores: (valores) => {
    const conjunto = new Set(valores);
    return {
      demanda: (d) => casa(conjunto, d.subsetorId),
      tarefa: (t) => casa(conjunto, t.subsetor_id),
      peca: (p) => casa(conjunto, p.subsetorId),
    };
  },
  postos: (valores) => {
    const conjunto = new Set(valores);
    return {
      demanda: (d) => casa(conjunto, d.postoId) || tarefasDe(d).some((t) => t.posto_id && conjunto.has(t.posto_id)),
      tarefa: (t) => casa(conjunto, t.posto_id),
      peca: (p) => casa(conjunto, p.postoId),
    };
  },
  colaboradores: (valores) => {
    const conjunto = new Set(valores);
    return {
      demanda: (d, ctx) => casaAlgum(conjunto, colaboradoresDaDemanda(d, ctx)),
      tarefa: (t) => casa(conjunto, t.colaborador_id),
      peca: (p) => casa(conjunto, p.colaboradorId),
    };
  },
  clientes: porCampoNormalizado("cliente"),
  pedidos: porCampoNormalizado("pedido"),
  lotes: porCampoNormalizado("lote"),
  pendencias: (valores) => {
    const conjunto = new Set(valores);
    return { demanda: (d) => (d.pendencias || []).some((p) => conjunto.has(p.codigo)) };
  },
  statusAmbiente: (valores) => {
    const conjunto = new Set(valores);
    return { demanda: (d) => conjunto.has(d.statusApp) };
  },
  statusTarefa: (valores) => {
    const conjunto = new Set(valores);
    return {
      demanda: (d, ctx) => (ctx.tarefasPorAmbiente.get(d.chave) || []).some((t) => conjunto.has(t.status)),
      tarefa: (t) => conjunto.has(t.status),
    };
  },
  tarefas: (valor) => ({
    demanda: (d) => (tarefasDe(d).length > 0) === (valor === "com"),
    peca: (p) => (tarefasDe(p).length > 0) === (valor === "com"),
  }),
  pecasFora: (valor) => ({
    demanda: (d) => ((d.pecasFora || 0) > 0) === (valor === "com"),
    peca: (p) => Boolean(p.fora) === (valor === "com"),
  }),
  atrasadas: (valor) => ({
    demanda: (d) => d.atrasada === (valor === "sim"),
    tarefa: (t, ctx) => tarefaAtrasada(t, ctx.hoje) === (valor === "sim"),
    peca: (p) => Boolean(p.atrasada) === (valor === "sim"),
  }),
  prazo: (periodo) => ({
    demanda: (d) => dentroDoPeriodo(d.prazoEfetivo, periodo),
    tarefa: (t) => dentroDoPeriodo(t.data_limite, periodo),
    peca: (p) => dentroDoPeriodo(p.prazoEfetivo, periodo),
  }),
};

// Filtros de ambiente valem para a tarefa e para a peça pelo ambiente delas.
const DE_AMBIENTE = new Set(["clientes", "pedidos", "lotes", "pendencias", "statusAmbiente", "etapas"]);

function predicadosAtivos(filtros) {
  return DEFINICOES_FILTROS.filter((d) => filtroAtivo(d, filtros[d.id])).map((d) => {
    const criterio = CRITERIOS[d.id](filtros[d.id]);
    if (!DE_AMBIENTE.has(d.id) || !criterio.demanda) return criterio;
    return {
      ...criterio,
      tarefa: criterio.tarefa || viaAmbiente(criterio.demanda, ambienteDaTarefa),
      peca: criterio.peca || viaAmbiente(criterio.demanda, ambienteDaPeca),
    };
  });
}

/**
 * `{demandas, tarefas, pecas, tipos}` que passam em TODOS os filtros ativos.
 *
 * `colecoes` = {demandas (combinadas, `combinar.js`), tarefas (TarefaResumo[],
 * abertas e recentes), pecas (combinadas, `pecas.js`)}.
 * `opcoes` = {estado, hoje} — `estado` serve para achar nomes (busca) e os
 * colaboradores de cada posto.
 *
 * `tipos` é o que `tiposDeItemAtivos` devolveu: a tela desenha só esses
 * blocos. A coleção de um tipo desligado volta VAZIA (e não filtrada).
 */
export function aplicarFiltros(colecoes, filtros, { estado = null, hoje = new Date() } = {}) {
  const listaDemandas = Array.isArray(colecoes?.demandas) ? colecoes.demandas : [];
  const listaTarefas = Array.isArray(colecoes?.tarefas) ? colecoes.tarefas : [];
  const listaPecas = Array.isArray(colecoes?.pecas) ? colecoes.pecas : [];
  const normalizados = normalizarFiltros(filtros);
  const tipos = tiposDeItemAtivos(normalizados);
  const predicados = predicadosAtivos(normalizados);

  const ctx = montarContexto(listaDemandas, listaTarefas, estado, hoje);
  const passa = (lado, item) => predicados.every((p) => !p[lado] || p[lado](item, ctx));
  const filtrar = (lista, lado, tiposDoLado) =>
    tiposDoLado.some((tipo) => tipos.includes(tipo)) ? lista.filter((item) => passa(lado, item)) : [];

  return {
    demandas: filtrar(listaDemandas, "demanda", TIPOS_QUE_USAM_DEMANDAS),
    tarefas: filtrar(listaTarefas, "tarefa", [TIPO_ITEM.TAREFA]),
    pecas: filtrar(listaPecas, "peca", [TIPO_ITEM.PECA]),
    tipos,
  };
}
